import { Carousel } from 'antd';
import { CarouselRef } from 'antd/lib/carousel';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classnames from 'classnames';
import { RevxCircleLabel } from 'components/RevxCircleLabel';
import { SkuType } from 'domains/skuDomain';
import { useCanvaPublishDraft } from 'hooks';
import { findIndex, sortBy } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import './CompanyGallerySlick.less';

/**
 * Category a Canva publish should land on when the company has one: those are the SKUs the Canva
 * app is meant to publish into, so the buyer starts there instead of on the whole catalogue.
 */
const CANVA_DEFAULT_LABEL_NAME = 'product gallery';

export type Props = {
  className?: string;
};

const carouselPageSize = 8;
const settings = {
  infinite: false,
  speed: 300,
  slidesToShow: carouselPageSize,
  slidesToScroll: 1,
};

export const CompanyGallerySlick = (props: Props) => {
  const dispatch = useAppDispatch();
  const selectedSkuType = useAppSelector(
    (state) => state.companyGallery.selectedSkuType
  );
  const selectedSkuLoading = useAppSelector(
    (state) => state.loading.effects.gallery.getCompanySkusByLabel
  );
  const [skuTypesFilter, setSkuTypesFilter] = useState<SkuType[]>([]);

  const carouselRef = useRef<CarouselRef>(null);
  const companyLabels = useAppSelector((state) => state.gallery.companyLabels);
  // This page loads its own copy: the counts follow the ?skuIds= / ?skuTags= filter, and a Canva
  // SSO buyer has no shared companyLabels at all. The shared ones stand in until it lands.
  const pageCompanyLabels = useAppSelector(
    (state) => state.companyGallery.pageCompanyLabels
  );
  const allGalleryLabels = pageCompanyLabels ?? companyLabels;
  const { isCanvaPublish } = useCanvaPublishDraft();
  const canvaDefaultApplied = useRef(false);
  useEffect(() => {
    const products = sortBy(
      allGalleryLabels.filter(
        (item) => item.skuCount > 0 && item.skuLabel.active
      ),
      (item) => {
        return item.skuLabel.name;
      }
    );
    const preConfigSku = (products || []).map((item) => {
      return {
        id: item.skuLabel.id,
        name: item.skuLabel.name,
        ...item,
      };
    });
    if (preConfigSku.length > 0) {
      const preConfigSkuTypes = [...preConfigSku];
      setSkuTypesFilter(preConfigSkuTypes);
    }
    // eslint-disable-next-line
  }, [allGalleryLabels]);
  useEffect(() => {
    // Once only: re-applying it would fight the buyer every time they clear the category.
    if (isCanvaPublish && !canvaDefaultApplied.current && !selectedSkuType) {
      const defaultLabel = skuTypesFilter.find(
        (item) =>
          item.name?.trim().toLowerCase() === CANVA_DEFAULT_LABEL_NAME &&
          Number(item.skuCount) > 0
      );
      if (defaultLabel) {
        canvaDefaultApplied.current = true;
        dispatch.companyGallery.setState({ selectedSkuType: defaultLabel });
      }
    }
  }, [dispatch, isCanvaPublish, selectedSkuType, skuTypesFilter]);
  useEffect(() => {
    const selectedIndex = findIndex(
      skuTypesFilter,
      (item) => item.id === selectedSkuType?.id
    );
    if (selectedIndex >= 0) {
      const maxIndex = Math.max(0, skuTypesFilter.length - carouselPageSize);
      setTimeout(() => {
        carouselRef.current?.goTo(Math.min(selectedIndex, maxIndex));
      }, 300);
    }
  }, [selectedSkuType?.id, skuTypesFilter]);
  return (
    <div
      className={classnames('company-gallery-slick', props.className)}
      hidden={!skuTypesFilter.length}
    >
      <div className="company-gallery-slick-list">
        <Carousel {...settings} dots={false} arrows={true} ref={carouselRef}>
          {skuTypesFilter.map((filter, index) => {
            const isFirst = index === 0;
            const isLast = index === skuTypesFilter.length - 1;

            return (
              <div key={filter.id}>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {skuTypesFilter.length > 1 && (
                    <div
                      style={{
                        position: 'absolute',
                        borderTop: '1px solid #bcbdc0',
                        height: '1px',
                        top: '58px',
                        left: isFirst ? '50%' : '0',
                        width: isFirst || isLast ? '50%' : '100%',
                        zIndex: 0,
                      }}
                    />
                  )}
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      display: 'inline-block',
                    }}
                  >
                    <RevxCircleLabel
                      text={String(filter.skuCount)}
                      name={filter.name}
                      checked={Boolean(
                        selectedSkuType && selectedSkuType.id === filter.id
                      )}
                      disabled={selectedSkuLoading}
                      onChange={(checked) => {
                        if (checked) {
                          dispatch.companyGallery.setState({
                            selectedSkuType: filter,
                          });
                        } else {
                          dispatch.companyGallery.setState({
                            selectedSkuType: null,
                          });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </Carousel>
      </div>
    </div>
  );
};
